import { useEffect, useMemo, useState } from "react";
import { X, Download, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fetchAuthOpts, formatApiError } from "@/lib/api";

const KIND_OPTIONS = [
  { value: "both", label: "Facturas y albaranes" },
  { value: "invoice", label: "Facturas" },
  { value: "delivery_note", label: "Albaranes" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "Todos (excluye anulados)" },
  { value: "emitido", label: "Emitido" },
  { value: "pagado", label: "Pagado" },
  { value: "pendiente", label: "Pendiente" },
  { value: "anulado", label: "Anulado" },
];

function fmt(n) {
  const v = Number(n || 0);
  return v.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function firstDayOfMonthISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export default function BillingExportDialog({ onClose }) {
  const [kind, setKind] = useState("both");
  const [dateFrom, setDateFrom] = useState(firstDayOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [status, setStatus] = useState("all");
  const [customerId, setCustomerId] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const [customers, setCustomers] = useState([]);
  const [payMethods, setPayMethods] = useState([]);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get("/settings").then((r) => setPayMethods(r.data.payment_methods || [])).catch(() => {});
  }, []);

  const customerMatches = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return [];
    return customers
      .filter((c) => {
        const blob = `${c.name || ""} ${c.tax_id || ""} ${c.email || ""}`.toLowerCase();
        return blob.includes(q);
      })
      .slice(0, 8);
  }, [customers, customerQuery]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId) || null,
    [customers, customerId]
  );

  const buildParams = () => {
    const params = {};
    if (kind) params.kind = kind;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (status) params.status = status;
    if (customerId) params.customer_id = customerId;
    if (paymentMethod) params.payment_method = paymentMethod;
    return params;
  };

  const runPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await api.get("/billing/export/preview", { params: buildParams() });
      setRows(r.data.rows || []);
      setSummary(r.data.summary || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingPreview(false);
    }
  };

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const usp = new URLSearchParams(buildParams());
      const r = await fetch(
        `${api.defaults.baseURL}/billing/export/xlsx?${usp.toString()}`,
        fetchAuthOpts()
      );
      if (!r.ok) {
        const txt = await r.text();
        toast.error(txt || "Error generando Excel");
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facturacion_${dateFrom || "inicio"}_${dateTo || "hoy"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch {
      toast.error("Error descargando Excel");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="billing-export-dialog"
    >
      <div
        className="bg-white border border-[#111110] shadow-hard w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E0]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D]">
              // Configuración › Facturación
            </div>
            <div className="font-display font-bold text-base">Exportar facturación</div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#F7F7F4]"
            data-testid="billing-export-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {/* Filtros */}
          <div className="p-5 border-b border-[#E5E5E0] bg-[#FAFAF7]">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <FilterField label="Tipo de documento">
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                  className="w-full ds-card px-2 py-1.5 text-sm rounded-none focus:border-[#6E8B3D] outline-none"
                  data-testid="billing-export-kind"
                >
                  {KIND_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="Fecha desde">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full ds-card px-2 py-1.5 text-sm rounded-none focus:border-[#6E8B3D] outline-none"
                  data-testid="billing-export-date-from"
                />
              </FilterField>

              <FilterField label="Fecha hasta">
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full ds-card px-2 py-1.5 text-sm rounded-none focus:border-[#6E8B3D] outline-none"
                  data-testid="billing-export-date-to"
                />
              </FilterField>

              <FilterField label="Estado">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full ds-card px-2 py-1.5 text-sm rounded-none focus:border-[#6E8B3D] outline-none"
                  data-testid="billing-export-status"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="Cliente">
                {selectedCustomer ? (
                  <div className="flex items-center gap-2 ds-card px-2 py-1.5 text-sm">
                    <span className="truncate flex-1">{selectedCustomer.name}</span>
                    <button
                      onClick={() => {
                        setCustomerId("");
                        setCustomerQuery("");
                      }}
                      className="p-0.5 hover:bg-[#F7F7F4]"
                      data-testid="billing-export-customer-clear"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8C8C82]" />
                    <input
                      type="text"
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      placeholder="Buscar cliente..."
                      className="w-full ds-card pl-7 pr-2 py-1.5 text-sm rounded-none focus:border-[#6E8B3D] outline-none"
                      data-testid="billing-export-customer-search"
                    />
                    {customerQuery && customerMatches.length > 0 && (
                      <div className="absolute z-10 top-full left-0 right-0 mt-1 ds-card shadow-hard max-h-48 overflow-y-auto">
                        {customerMatches.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => {
                              setCustomerId(c.id);
                              setCustomerQuery("");
                            }}
                            className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#F7F7F4] border-b border-[#E5E5E0] last:border-b-0"
                            data-testid={`billing-export-customer-pick-${c.id}`}
                          >
                            <div className="font-medium">{c.name}</div>
                            {c.tax_id && <div className="text-[10px] text-[#8C8C82] font-mono">{c.tax_id}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </FilterField>

              <FilterField label="Método de pago">
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full ds-card px-2 py-1.5 text-sm rounded-none focus:border-[#6E8B3D] outline-none"
                  data-testid="billing-export-payment-method"
                >
                  <option value="">Todos</option>
                  {payMethods.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </FilterField>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={runPreview}
                disabled={loadingPreview}
                className="bg-[#111110] text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#2a2a28] disabled:opacity-50"
                data-testid="billing-export-preview-btn"
              >
                {loadingPreview ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Buscar documentos
              </button>
              <button
                onClick={downloadXlsx}
                disabled={!summary || summary.count === 0 || downloading}
                className="bg-[#6E8B3D] text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#556C2E] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="billing-export-download-btn"
              >
                {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Descargar Excel
              </button>
            </div>
          </div>

          {/* Totales */}
          {summary && (
            <div className="px-5 py-3 border-b border-[#E5E5E0] bg-white grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
              <TotalBox label="Documentos" value={summary.count} testid="billing-export-total-count" />
              <TotalBox label="Base imponible" value={`${fmt(summary.total_base)} €`} testid="billing-export-total-base" />
              <TotalBox label="IVA" value={`${fmt(summary.total_tax)} €`} testid="billing-export-total-tax" />
              <TotalBox label="Descuentos" value={`${fmt(summary.total_discount)} €`} testid="billing-export-total-discount" />
              <TotalBox label="Total facturado" value={`${fmt(summary.total_total)} €`} highlight testid="billing-export-total-total" />
            </div>
          )}

          {/* Tabla */}
          <div className="p-5">
            {!summary ? (
              <div className="text-center text-sm text-[#8C8C82] py-10">
                Ajusta los filtros y pulsa <strong>Buscar documentos</strong> para ver el listado.
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-sm text-[#8C8C82] py-10">
                No se encontraron documentos con los filtros seleccionados.
              </div>
            ) : (
              <div className="overflow-x-auto border border-[#E5E5E0]">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0] sticky top-0">
                    <tr>
                      <Th>Tipo</Th>
                      <Th>Nº</Th>
                      <Th>Fecha</Th>
                      <Th>Cliente</Th>
                      <Th>NIF/CIF</Th>
                      <Th>Reparación</Th>
                      <Th>Método pago</Th>
                      <Th right>Base</Th>
                      <Th right>IVA</Th>
                      <Th right>Dto.</Th>
                      <Th right>Total</Th>
                      <Th>Estado</Th>
                    </tr>
                  </thead>
                  <tbody data-testid="billing-export-rows">
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-[#E5E5E0] hover:bg-[#FAFAF7]">
                        <Td>{r.kind_label}</Td>
                        <Td mono>{r.code}</Td>
                        <Td mono>{r.date}</Td>
                        <Td>{r.customer_name}</Td>
                        <Td mono>{r.customer_tax_id}</Td>
                        <Td mono>{r.repair_code}</Td>
                        <Td>{r.payment_method}</Td>
                        <Td right>{fmt(r.subtotal)} €</Td>
                        <Td right>{fmt(r.tax)} €</Td>
                        <Td right>{fmt(r.discount)} €</Td>
                        <Td right bold>{fmt(r.total)} €</Td>
                        <Td>
                          <span className="inline-block px-1.5 py-0.5 bg-[#F7F7F4] border border-[#E5E5E0] text-[10px] uppercase">
                            {r.status || "—"}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#FAFAF7] border-t-2 border-[#111110] font-bold">
                    <tr>
                      <td colSpan={7} className="px-2 py-2 text-right">Totales</td>
                      <td className="px-2 py-2 text-right">{fmt(summary.total_base)} €</td>
                      <td className="px-2 py-2 text-right">{fmt(summary.total_tax)} €</td>
                      <td className="px-2 py-2 text-right">{fmt(summary.total_discount)} €</td>
                      <td className="px-2 py-2 text-right">{fmt(summary.total_total)} €</td>
                      <td className="px-2 py-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[9px] uppercase tracking-widest text-[#525252] block mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function TotalBox({ label, value, highlight, testid }) {
  return (
    <div
      className={`border px-3 py-2 ${highlight ? "border-[#6E8B3D] bg-[#F0F4E8]" : "border-[#E5E5E0] bg-[#FAFAF7]"}`}
      data-testid={testid}
    >
      <div className="font-mono text-[9px] uppercase tracking-widest text-[#525252]">{label}</div>
      <div className={`font-display font-bold mt-0.5 ${highlight ? "text-[#556C2E]" : "text-[#111110]"}`}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, right }) {
  return (
    <th className={`px-2 py-2 font-mono text-[9px] uppercase tracking-widest text-[#525252] ${right ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function Td({ children, right, mono, bold }) {
  return (
    <td
      className={`px-2 py-1.5 ${right ? "text-right" : "text-left"} ${mono ? "font-mono" : ""} ${bold ? "font-bold" : ""}`}
    >
      {children}
    </td>
  );
}
